import { useState } from 'react';
import { api } from '../api';
import DateTimePicker from './DateTimePicker';

// Fields required before a project can be marked Awarded, in display order.
// Mirrors the server gate in server/routes/projects.js (missingAwardFields).
// ProjectCost is the "Bid Amount" — NOT "Projected Bid Amount".
const AWARD_FIELDS = [
  { key: 'EstStartDate', label: 'Est. Project Start Date', type: 'date' },
  { key: 'ProjectCost', label: 'Bid Amount ($)', type: 'money' },
  { key: 'DateAwarded', label: 'Date Awarded', type: 'date' },
];

function isMissing(field, value) {
  if (field.type === 'money') return value === '' || value === null || value === undefined || Number(value) === 0;
  return !value;
}

// Prompt the user to fill the award-required fields when flipping a project to
// Awarded. Only the still-missing fields are shown; submitting saves them
// alongside the status change in a single PATCH.
export default function AwardModal({ project, awardedStatusId, onClose, onAwarded }) {
  const missingFields = AWARD_FIELDS.filter(f => isMissing(f, project[f.key]));
  const [values, setValues] = useState(() =>
    Object.fromEntries(missingFields.map(f => [f.key, '']))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, v) => setValues(prev => ({ ...prev, [key]: v }));
  const stillMissing = missingFields.filter(f => isMissing(f, values[f.key]));

  const submit = () => {
    if (stillMissing.length) {
      setError('Please fill in every field below before marking this bid Awarded.');
      return;
    }
    setSaving(true);
    setError('');
    api.updateStatus(project.ProjectId, awardedStatusId, values)
      .then(() => onAwarded && onAwarded())
      .catch(err => { setError(err.message || 'Could not mark Awarded.'); setSaving(false); });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
        <div className="modal-header">
          <h2>Mark as Awarded 🏆</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0, color: 'var(--color-gray-600)', fontSize: '13px' }}>
            Before <strong>{project.ProjectName}</strong> can be marked Awarded, these
            required fields need values:
          </p>
          {missingFields.map(f => (
            <div className="form-group" key={f.key} style={{ marginBottom: '12px' }}>
              <label>{f.label} <span className="required">*</span></label>
              {f.type === 'date' ? (
                <DateTimePicker value={values[f.key]} onChange={v => set(f.key, v)} />
              ) : (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={values[f.key]}
                  onChange={e => set(f.key, e.target.value)}
                  placeholder="0.00"
                />
              )}
            </div>
          ))}
          {error && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-accent btn-sm" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Mark Awarded'}
          </button>
        </div>
      </div>
    </div>
  );
}
